import { toastMessage } from "../../../utils/toast";

/**
 * Turning a server refusal into something the user can act on.
 *
 * WHY THIS EXISTS RATHER THAN `errorToastMessage`. The platform's
 * `errorToastMessage` reads `error.message` off an axios error, and an RTK
 * Query error is not one: its `message` is the i18n KEY the server returned
 * (`randomization.LIST_EXHAUSTED`), so passing it straight through would put
 * a dotted identifier in a toast. Section 23 wrote a message for every one of
 * those keys precisely because the useful ones say what to do next — "no
 * randomization slots remain for this stratum, extend the list before
 * continuing" — and losing that to "SOMETHING_WENT_WRONG" throws away the
 * entire error catalogue.
 *
 * The interpolation parameters come from the error's `data` payload, which is
 * where `fail(key, status, data)` puts them server-side, so `{{stratum}}` and
 * `{{variable}}` fill in.
 *
 * @category Randomization
 */

export interface RandomizationErrorLike {
  status?: number;
  /** The i18n key from spec section 23 — but see `messageText`: the platform
   *  also sends arrays and objects here. */
  message?: unknown;
  /** Interpolation parameters, and/or `errors` for field mapping. */
  data?: unknown;
}

type Translate = (key: string, params?: Record<string, unknown>) => string;

/** Strips the namespace prefix the server sends, since the lookup happens
 *  inside the `randomization` namespace already. */
/**
 * The server's `message` is NOT always a string.
 *
 * The platform's ValidationPipe returns an ARRAY, sometimes of objects
 * (`utils/toast.ts` has handled that shape since long before this module), a
 * nested rejection can return an object, and a transport failure returns
 * nothing at all. `axiosBaseQuery` copies whatever it finds straight through
 * as `message`, so every reader here has to cope with all four.
 *
 * Reading it as a string is what produced `key.startsWith is not a function`
 * — a white-screen application error in place of the message the server
 * actually sent, on any refusal from the list or report endpoints.
 */
function messageText(raw: unknown): string {
  if (typeof raw === "string") return raw;
  if (Array.isArray(raw)) {
    return raw
      .map((entry) => {
        if (typeof entry === "string") return entry;
        if (entry && typeof entry === "object") {
          return Object.values(entry as Record<string, unknown>)
            .map((value) => (Array.isArray(value) ? value.join(", ") : String(value)))
            .join(", ");
        }
        return entry == null ? "" : String(entry);
      })
      .filter(Boolean)
      .join("; ");
  }
  if (raw && typeof raw === "object") {
    const nested = (raw as { message?: unknown }).message;
    if (typeof nested === "string") return nested;
    try {
      return JSON.stringify(raw);
    } catch {
      return "";
    }
  }
  return "";
}

/**
 * The refusal's KEY — `LIST_EXHAUSTED`, `STRATUM_UNRESOLVED` — not its message.
 *
 * THE METADATA IS READ FIRST, AND THE MESSAGE IS ONLY A FALLBACK. This used to
 * read `message` alone and strip a `randomization.` prefix. That worked while
 * the server's i18n file was the retired module's nineteen keys: an unmapped
 * key fell through as raw text and looked like a key. The file was completed
 * on 13 September and `message` became a rendered English sentence — a Spanish
 * one on the Spanish locale — so every caller comparing this against a key
 * silently began returning false, with nothing failing to show it.
 *
 * `randomization-failure.ts` on the server now stamps the bare key into
 * `metadata.key` on every refusal this module raises, and `axiosBaseQuery`
 * surfaces `metadata` as `error.data`. The message fallback stays for
 * refusals raised outside this module, such as the platform's ValidationPipe.
 */
export function randomizationErrorKey(error: unknown): string {
  const typed = (error ?? {}) as RandomizationErrorLike;
  const stamped = (typed.data as { key?: unknown } | undefined)?.key;
  if (typeof stamped === "string" && stamped.length > 0) return stamped;
  const key = messageText(typed.message);
  return key.startsWith("randomization.") ? key.slice("randomization.".length) : key;
}

export function randomizationErrorMessage(error: unknown, t: Translate): string {
  const typed = (error ?? {}) as RandomizationErrorLike;
  const key = randomizationErrorKey(error);
  if (!key) return t("ui.errorTitle");
  const params = (typed.data ?? {}) as Record<string, unknown>;
  // `defaultValue` is the raw key, so an unrecognised server error still shows
  // something identifiable rather than an empty toast.
  return t(key, { ...params, defaultValue: messageText(typed.message) || key });
}

export function showRandomizationError(error: unknown, t: Translate): void {
  toastMessage("error", randomizationErrorMessage(error, t));
}

export function showRandomizationSuccess(message: string): void {
  toastMessage("success", message);
}

/**
 * Turns a server refusal into the Formik field errors of spec 21.8.
 *
 * The validation modules return `data.errors` as `{ field, code, message }[]`
 * and their field paths match the form field names deliberately —
 * `options[2].probabilityPpm` on the server is the same string the Formik
 * field uses — so the mapping is a copy rather than a translation table that
 * would silently drift. Anything without a recognised field becomes a
 * form-level alert instead of being dropped.
 */
export function mapServerErrors(
  error: unknown,
  t?: Translate,
): {
  fieldErrors: Record<string, string>;
  formErrors: string[];
  /** True when the server offered the sparse-strata acknowledgement. */
  acknowledgeable: boolean;
} {
  const typed = (error ?? {}) as RandomizationErrorLike;
  const data = (typed.data ?? {}) as {
    errors?: { field?: string; key?: string; params?: Record<string, unknown>; message?: string }[];
    field?: string;
    acknowledgeable?: boolean;
  };
  const fieldErrors: Record<string, string> = {};
  const formErrors: string[] = [];

  // THE SERVER SENDS `{ key, params?, field? }`, not `{ field, code, message }`.
  //
  // `ValidationError` in `randomization/validation/config-validation.ts` has no
  // `message` at all, so reading one gave `undefined` for every entry: the loop
  // mapped nothing, pushed nothing, and eight field-level errors collapsed into
  // a single unhelpful form alert. Spec 21.8 requires each error on the Formik
  // field its `field` names.
  //
  // `t` is optional so existing callers keep compiling; without it the raw key
  // is shown, which is still more use than nothing.
  for (const issue of data.errors ?? []) {
    const text = issue.key
      ? (t ? t(issue.key, issue.params) : issue.key)
      : issue.message;
    if (issue.field && text) fieldErrors[issue.field] = text;
    else if (text) formErrors.push(text);
  }

  // A single-field rejection (the parameter guard, a duplicate code) arrives
  // as `data.field` rather than an array.
  const topLevel = randomizationErrorMessage(error, t ?? ((k: string) => k));
  if (data.field && topLevel && !fieldErrors[data.field]) {
    fieldErrors[data.field] = topLevel;
  }
  if (
    Object.keys(fieldErrors).length === 0 &&
    formErrors.length === 0 &&
    topLevel
  ) {
    formErrors.push(topLevel);
  }
  return {
    fieldErrors,
    formErrors,
    acknowledgeable: data.acknowledgeable === true,
  };
}
